import React,{useCallback,useEffect,useState} from 'react';
import {Copy,Share2,PackagePlus} from 'lucide-react';

const INVITE_PROFILES={
  operator:{role:'operator',permissions:{},label:'Operador'},
  inventory:{role:'operator',permissions:{pdv_catalog:true,pdv_inventory:true},label:'Produtos e estoque'},
  viewer:{role:'viewer',permissions:{},label:'Consulta'},
  admin:{role:'admin',permissions:{},label:'Administrador'},
};
export function smartPdvInviteProfile(profile){
  return INVITE_PROFILES[profile] || INVITE_PROFILES.operator;
}

/**
 * Organization-scoped administration. No direct writes to organization_members:
 * every invitation and role change is checked again in Supabase RPCs.
 */
export default function StaffManagement({organization,client}){
 const [team,setTeam]=useState(null),[busy,setBusy]=useState(false),
 [name,setName]=useState(''),[email,setEmail]=useState(''),[profile,setProfile]=useState('operator'),
 [message,setMessage]=useState(''),[error,setError]=useState(''),[link,setLink]=useState('');
 const refresh=useCallback(async()=>{
  if(!organization?.id)return;
  const {data,error:e}=await client.rpc('pdv_team_snapshot',{p_org:organization.id});
  if(e)throw new Error('Não foi possível carregar a equipe.');
  if(data?.organization_id!==organization.id||!Array.isArray(data.members))throw new Error('Empresa inválida.');
  setTeam(data);
 },[client,organization?.id]);
 useEffect(()=>{let live=true;setTeam(null);setError('');setLink('');
  if(!organization?.id)return;
  client.rpc('pdv_team_snapshot',{p_org:organization.id}).then(({data,error:e})=>{
   if(!live)return;
   if(e||data?.organization_id!==organization.id)setError('Não foi possível consultar a equipe.');
   else setTeam(data);
  }).catch(()=>{if(live)setError('Não foi possível consultar a equipe.');});
  return()=>{live=false};
 },[organization?.id,client]);
 async function invite(e){
  e.preventDefault();setBusy(true);setError('');setMessage('');setLink('');
  const chosen=smartPdvInviteProfile(profile);
  try{
   const {data,error:x}=await client.rpc('pdv_invite_operator_named',{
    p_org:organization.id,p_name:name.trim(),p_email:email.trim().toLowerCase(),
    p_role:chosen.role,p_permissions:chosen.permissions
   });
   if(x||!data?.ok||!data?.activation_url)throw Error('Falha ao criar convite.');
   setLink(data.activation_url);setEmail('');setName('');
   setMessage('Convite criado para '+name.trim()+'. Compartilhe o link apenas com o funcionário indicado.');
   await refresh();
  }catch(e){setError(e.message)}finally{setBusy(false)}
 }
 async function copyInvite(){
   try {
     await navigator.clipboard.writeText(link);
     setMessage('Link copiado. Envie individualmente ao funcionário.');
   }catch {
     setMessage('Selecione o endereço do convite e copie manualmente.');
   }
 }
 async function shareInvite(){
   if(!link)return;
   if(typeof navigator.share==='function'){
     try{
       await navigator.share({
         title:'Convite SmartPDV - '+organization.name,
         text:'Convite individual para acessar o SmartPDV de '+organization.name+
           '. Use seu próprio e-mail e conclua a confirmação da conta.',
         url:link,
       });
       setMessage('Compartilhamento solicitado. O acesso só será ativado após o aceite autenticado.');
       return;
     }catch(e){
       if(e?.name==='AbortError')return;
     }
   }
   await copyInvite();
 }
 async function modify(member,nextRole,nextStatus){
  if(!window.confirm('Confirmar alteração de acesso deste operador?'))return;
  setBusy(true);setError('');setMessage('');
  try{
   const {data,error:x}=await client.rpc('pdv_team_set_member',{
    p_org:organization.id,p_user:member.user_id,
    p_role:nextRole,p_status:nextStatus
   });
   if(x||!data?.ok)throw Error('Não foi possível alterar as permissões.');
   setMessage('Acesso do operador atualizado.');await refresh();
  }catch(e){setError(e.message)}finally{setBusy(false)}
 }
 return <section className="admin">
  <div className="pageTitle"><div>
   <p className="eyebrow">EQUIPE POR EMPRESA</p><h1>Operadores e permissões</h1>
   <p className="muted">Convide funcionários para {organization.name}. Cada empresa mantém usuários e permissões próprios.</p>
  </div></div>
  <section className="panel">
   <h2>Cadastrar funcionário e gerar link</h2>
   <p className="muted">Cadastre pelo nome, escolha as permissões e gere um link seguro.
     O e-mail individual é necessário para confirmar quem aceita o convite.</p>
   <form onSubmit={invite} className="staffForm">
    <label>Nome do funcionário
     <input type="text" required autoComplete="name" minLength={2} maxLength={100}
       value={name} onChange={e=>setName(e.target.value)}
       placeholder="Nome e sobrenome"/>
    </label>
    <label>E-mail do funcionário
     <input type="email" required value={email} onChange={e=>setEmail(e.target.value)} placeholder="operador@empresa.com"/>
    </label>
    <label>Perfil de acesso
     <select value={profile} onChange={e=>setProfile(e.target.value)}>
      <option value="operator">Operador</option>
      <option value="inventory">Produtos e estoque (não administrador)</option>
      <option value="viewer">Consulta</option>
      <option value="admin">Administrador</option>
     </select>
    </label>
    <p className="staffProfileHelp" role="note">
      {profile==='inventory'
       ? <><PackagePlus size={18}/> Pode cadastrar produtos, conferir DANFE/NF-e e ajustar estoque
           quando a função estiver habilitada. Não pode gerenciar usuários nem configurações.
           A entrada automática por NF-e continua sujeita à homologação.</>
       : profile==='admin'
       ? 'Administrador gerencia usuários, configurações e permissões. Conceda apenas a gestores.'
       : profile==='viewer'
       ? 'Perfil de consulta, sem poderes administrativos.'
       : 'Operador padrão, sem concessão adicional de cadastro e gestão do estoque.'}
    </p>
    <button type="submit" disabled={busy||name.trim().length<2||!email.trim()}>
      Gerar link de acesso
    </button>
   </form>
   {link&&<div className="staffInviteLink" role="status">
    <strong>Link individual criado — válido por 72 horas</strong>
    <p>Somente a pessoa que acessar com o e-mail convidado e confirmar a conta poderá aceitar.</p>
    <input aria-label="Link individual de ativação" readOnly value={link} onFocus={e=>e.target.select()}/>
    <div className="staffInviteActions">
      <button type="button" onClick={copyInvite}><Copy size={17}/> Copiar link</button>
      <button type="button" onClick={shareInvite}><Share2 size={17}/> Compartilhar link</button>
    </div>
   </div>}
  </section>
  <section className="panel"><div className="line"><h2>Equipe cadastrada</h2>
   <button type="button" disabled={busy} onClick={()=>refresh().catch(()=>setError('Falha ao atualizar equipe.'))}>Atualizar</button></div>
   {!team?<p>Carregando equipe…</p>:
    <div className="staffList">{team.members.map(m=><div className="staffRow" key={m.user_id}>
     <div><strong>{m.display_name||m.email||'Usuário'}</strong>
       <small>{m.email} · {m.status==='active'?'Ativo':'Inativo'}</small>
       {m.role==='operator'&&m.permissions?.pdv_catalog===true&&m.permissions?.pdv_inventory===true&&
         <small>Perfil: Produtos e estoque</small>}
     </div>
     {m.role==='owner'?<span>Proprietário</span>:<div className="staffActions">
      <select aria-label={'Perfil de '+(m.email||'operador')} value={m.role}
       disabled={busy} onChange={e=>modify(m,e.target.value,m.status)}>
       <option value="admin">Administrador</option><option value="operator">Operador</option>
       <option value="viewer">Consulta</option>
      </select>
      <button type="button" disabled={busy} onClick={()=>modify(m,m.role,m.status==='active'?'inactive':'active')}>
       {m.status==='active'?'Desativar':'Ativar'}
      </button>
     </div>}
    </div>)}</div>}
   {!!team?.invitations?.length&&<details><summary>Convites recentes ({team.invitations.length})</summary>
    {team.invitations.map(i=><p key={i.id}>
      <strong>{i.display_name||'Sem nome'}</strong> · {i.email} · {i.role} · {i.status}
      {i.permissions?.pdv_catalog===true&&i.permissions?.pdv_inventory===true?' · Produtos e estoque':''}
    </p>)}
   </details>}
  </section>
  {message&&<p className="success" role="status">{message}</p>}
  {error&&<p className="error" role="alert">{error}</p>}
 </section>;
}
